import { useSiteReducedMotion, useScrollDirection } from "./motion-preferences";
import { useEffect, useRef, useState } from "react";

import { Pause, Play, MoveUpRight } from "lucide-react";

const geometryVertex = `
precision highp float;
attribute vec3 sample;
uniform float time;
uniform vec2 pointer;
uniform float aspect;
uniform float pixelRatio;
uniform float pointMode;
varying vec3 light;
varying float opacity;
vec3 field(vec2 uv) {
  float x=(uv.x-.5)*14.;
  float v=uv.y;
  float twist=(uv.x-.5)*5.8+.22*sin(time*.18);
  float width=v*(1.7+.4*sin(uv.x*3.14159));
  float wave=sin(uv.x*6.28318-.9+sin(time*.13)*.25);
  vec3 p=vec3(x,width*cos(twist)+wave*.85,width*sin(twist)+cos(uv.x*6.28318)*.6);
  p.y+=.1*x;
  float d=length(vec2(p.x-pointer.x*6.,p.y-pointer.y*2.5));
  p.z+=exp(-d*d*.24)*.55;
  p.y+=sin(uv.x*12.+v*2.-time*.35)*.07;
  return p;
}
void main() {
  vec2 uv=sample.xy;
  vec3 p=field(uv);
  vec3 du=field(uv+vec2(.002,0.))-p;
  vec3 dv=field(uv+vec2(0.,.002))-p;
  vec3 n=normalize(cross(du,dv));
  float yaw=pointer.x*.1;
  p.xz=mat2(cos(yaw),-sin(yaw),sin(yaw),cos(yaw))*p.xz;
  float tilt=pointer.y*.07;
  p.yz=mat2(cos(tilt),-sin(tilt),sin(tilt),cos(tilt))*p.yz;
  float portrait=1.-smoothstep(1.,1.7,aspect);
  float angle=portrait*.45;
  p.xy=mat2(cos(angle),-sin(angle),sin(angle),cos(angle))*p.xy;
  float depth=8.2+portrait*2.8-p.z;
  gl_Position=vec4(p.x*2.2/aspect,p.y*2.2,0.,depth);
  float satin=pow(abs(dot(n,normalize(vec3(-.4,1.,1.)))),9.);
  float rim=pow(1.-abs(n.z),3.);
  float signal=pow(max(0.,cos(uv.x*12.-time*.65+uv.y*.7)),22.);
  float edge=pow(abs(uv.y),5.);
  float depthLight=clamp((p.z+3.)/5.,.15,1.);
  light=mix(vec3(.47,.37,.10),vec3(1.,.96,.80),satin*.65+rim*.22+edge*.13);
  light+=vec3(.65,.49,.10)*signal*.65;
  opacity=(.13+satin*.58+rim*.2+signal*.15)*depthLight;
  opacity*=smoothstep(0.,.12,uv.x)*smoothstep(1.,.85,uv.x);
  if(pointMode>.5) {light=mix(vec3(.99,.87,.28),vec3(1.),sample.z);opacity=.35+sample.z*.45;}
  gl_PointSize=(1.2+sample.z*1.9)*pixelRatio*8./depth;
}`;
const geometryFragment = `
precision highp float;
varying vec3 light;
varying float opacity;
uniform float pointMode;
void main(){
 float alpha=opacity;
 if(pointMode>.5){float d=length(gl_PointCoord-.5);alpha*=1.-smoothstep(.1,.5,d);}
 gl_FragColor=vec4(light,alpha);
}`;
const screenVertex = `attribute vec2 position;varying vec2 uv;void main(){uv=position*.5+.5;gl_Position=vec4(position,0.,1.);}`;
const blurFragment = `
precision mediump float;
varying vec2 uv;
uniform sampler2D source;
uniform vec2 direction;
void main(){
 vec3 c=texture2D(source,uv).rgb*.227027;
 c+=texture2D(source,uv+direction*1.384615).rgb*.316216;
 c+=texture2D(source,uv-direction*1.384615).rgb*.316216;
 c+=texture2D(source,uv+direction*3.230769).rgb*.070270;
 c+=texture2D(source,uv-direction*3.230769).rgb*.070270;
 gl_FragColor=vec4(c,1.);
}`;
const compositeFragment = `
precision mediump float;
varying vec2 uv;
uniform sampler2D source;
uniform sampler2D bloom;
uniform float aspect;
void main(){
 vec2 p=(uv-.5)*vec2(aspect,1.);
 float halo=exp(-dot(p*vec2(.55,1.5),p*vec2(.55,1.5))*3.);
 vec3 background=mix(vec3(.012,.012,.012),vec3(.042,.038,.023),halo);
 vec3 color=texture2D(source,uv).rgb;
 vec3 glow=texture2D(bloom,uv).rgb;
 color=background+color*.95+glow*1.3;
 color=1.-exp(-color*1.6);
 float grain=fract(sin(dot(gl_FragCoord.xy,vec2(12.9898,78.233)))*43758.5453);
 gl_FragColor=vec4(color+(grain-.5)/255.,1.);
}`;

function StaticField() {
  return (
    <svg
      className="field-fallback"
      viewBox="0 0 1200 520"
      fill="none"
      aria-hidden="true"
    >
      {Array.from({ length: 70 }, (_, i) => {
        const v = (i / 69 - 0.5) * 2;
        const points = Array.from({ length: 140 }, (_, j) => {
          const u = j / 139,
            twist = (u - 0.5) * 5.8,
            width = v * (1.7 + 0.4 * Math.sin(u * Math.PI));
          const x = (u - 0.5) * 14,
            y =
              width * Math.cos(twist) +
              Math.sin(u * Math.PI * 2 - 0.9) * 0.85 +
              x * 0.1;
          const z = width * Math.sin(twist) + Math.cos(u * Math.PI * 2) * 0.6;
          return `${j ? "L" : "M"}${(600 + (x * 570) / (8.2 - z)).toFixed(1)},${(260 - (y * 570) / (8.2 - z)).toFixed(1)}`;
        }).join(" ");
        return (
          <path
            key={i}
            d={points}
            stroke={i % 5 === 0 ? "#fff0b1" : "#9c8433"}
            strokeOpacity={i % 5 === 0 ? 0.65 : 0.38}
            strokeWidth=".7"
          />
        );
      })}
    </svg>
  );
}

export function QuantumField() {
  const canvasRef = useRef(null),
    stageRef = useRef(null),
    requestRef = useRef(null);
  const reduced = useSiteReducedMotion();
  const direction = useScrollDirection();
  const [paused, setPaused] = useState(false),
    [motionEnabled, setMotionEnabled] = useState(false),
    [ready, setReady] = useState(false);
  const playing = !paused && (!reduced || motionEnabled);
  const flags = useRef({ paused: false, reduced: false });
  useEffect(() => {
    flags.current = {
      paused: !playing || direction === "up",
      reduced: reduced && !motionEnabled,
    };
    requestRef.current?.();
  }, [playing, reduced, motionEnabled, direction]);
  useEffect(() => {
    const canvas = canvasRef.current,
      stage = stageRef.current;
    const gl = canvas.getContext("webgl", {
      alpha: false,
      antialias: false,
      powerPreference: "low-power",
      depth: false,
      stencil: false,
    });
    if (!gl) return;
    let frame = 0,
      visible = true,
      disposed = false,
      last = 0,
      clock = 0,
      width = 1,
      height = 1,
      ratio = 1;
    const resources = {
      programs: [],
      shaders: [],
      buffers: [],
      textures: [],
      frames: [],
    };
    const makeProgram = (vertex, fragment) => {
      const program = gl.createProgram();
      resources.programs.push(program);
      for (const [type, source] of [
        [gl.VERTEX_SHADER, vertex],
        [gl.FRAGMENT_SHADER, fragment],
      ]) {
        const shader = gl.createShader(type);
        resources.shaders.push(shader);
        gl.shaderSource(shader, source);
        gl.compileShader(shader);
        if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS))
          throw new Error(gl.getShaderInfoLog(shader));
        gl.attachShader(program, shader);
      }
      gl.linkProgram(program);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS))
        throw new Error(gl.getProgramInfoLog(program));
      return program;
    };
    const clean = () => {
      resources.frames.forEach((x) => gl.deleteFramebuffer(x));
      resources.textures.forEach((x) => gl.deleteTexture(x));
      resources.buffers.forEach((x) => gl.deleteBuffer(x));
      resources.programs.forEach((x) => gl.deleteProgram(x));
      resources.shaders.forEach((x) => gl.deleteShader(x));
    };
    let scene, blur, composite;
    try {
      scene = makeProgram(geometryVertex, geometryFragment);
      blur = makeProgram(screenVertex, blurFragment);
      composite = makeProgram(screenVertex, compositeFragment);
    } catch (error) {
      console.warn("Quantum field uses its static fallback:", error.message);
      clean();
      return;
    }
    const buffer = (data) => {
      const b = gl.createBuffer();
      resources.buffers.push(b);
      gl.bindBuffer(gl.ARRAY_BUFFER, b);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(data), gl.STATIC_DRAW);
      return b;
    };
    const mobile = stage.clientWidth < 650,
      strands = mobile ? 76 : 144,
      segments = mobile ? 200 : 320;
    const vertices = [];
    for (let i = 0; i < strands; i++)
      for (let j = 0; j < segments; j++) {
        const v = (i / (strands - 1)) * 2 - 1;
        vertices.push(j / segments, v, 0, (j + 1) / segments, v, 0);
      }
    const lines = buffer(vertices),
      particles = [];
    for (let i = 0; i < (mobile ? 700 : 1500); i++) {
      const random = (n) => {
        const v = Math.sin(n * 127.1 + 311.7) * 43758.5453;
        return v - Math.floor(v);
      };
      particles.push(random(i), random(i + 1500) * 2 - 1, random(i + 3000));
    }
    const points = buffer(particles),
      quad = buffer([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);
    const target = () => {
      const texture = gl.createTexture(),
        fbo = gl.createFramebuffer();
      resources.textures.push(texture);
      resources.frames.push(fbo);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      return { texture, fbo, width: 1, height: 1 };
    };
    const main = target(),
      blurA = target(),
      blurB = target();
    const allocate = (t, w, h) => {
      t.width = w;
      t.height = h;
      gl.bindTexture(gl.TEXTURE_2D, t.texture);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        w,
        h,
        0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        null,
      );
      gl.bindFramebuffer(gl.FRAMEBUFFER, t.fbo);
      gl.framebufferTexture2D(
        gl.FRAMEBUFFER,
        gl.COLOR_ATTACHMENT0,
        gl.TEXTURE_2D,
        t.texture,
        0,
      );
    };
    const loc = (p, n) => gl.getUniformLocation(p, n);
    const uniforms = {
      time: loc(scene, "time"),
      pointer: loc(scene, "pointer"),
      aspect: loc(scene, "aspect"),
      pixelRatio: loc(scene, "pixelRatio"),
      pointMode: loc(scene, "pointMode"),
      blurSource: loc(blur, "source"),
      direction: loc(blur, "direction"),
      source: loc(composite, "source"),
      bloom: loc(composite, "bloom"),
      screenAspect: loc(composite, "aspect"),
    };
    const attributes = {
      sample: gl.getAttribLocation(scene, "sample"),
      blur: gl.getAttribLocation(blur, "position"),
      composite: gl.getAttribLocation(composite, "position"),
    };
    const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
    const bindAttribute = (b, attribute, size) => {
      gl.bindBuffer(gl.ARRAY_BUFFER, b);
      gl.enableVertexAttribArray(attribute);
      gl.vertexAttribPointer(attribute, size, gl.FLOAT, false, 0, 0);
    };
    const bindTarget = (t) => {
      gl.bindFramebuffer(gl.FRAMEBUFFER, t?.fbo || null);
      gl.viewport(0, 0, t?.width || width, t?.height || height);
    };
    const blurPass = (input, output, dx, dy) => {
      bindTarget(output);
      gl.useProgram(blur);
      bindAttribute(quad, attributes.blur, 2);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, input.texture);
      gl.uniform1i(uniforms.blurSource, 0);
      gl.uniform2f(uniforms.direction, dx, dy);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    };
    const draw = (now) => {
      if (disposed || gl.isContextLost()) return;
      const animate = !flags.current.paused && !flags.current.reduced;
      if (visible && !document.hidden) {
        if (now - last >= 32 || !animate) {
          if (animate && last) clock += Math.min((now - last) / 1000, 0.05);
          last = now;
          mouse.x += (mouse.tx - mouse.x) * 0.055;
          mouse.y += (mouse.ty - mouse.y) * 0.055;
          bindTarget(main);
          gl.clearColor(0, 0, 0, 1);
          gl.clear(gl.COLOR_BUFFER_BIT);
          gl.useProgram(scene);
          gl.enable(gl.BLEND);
          gl.blendFunc(gl.SRC_ALPHA, gl.ONE);
          gl.uniform1f(uniforms.time, flags.current.reduced ? 0 : clock);
          gl.uniform2f(
            uniforms.pointer,
            flags.current.reduced ? 0 : mouse.x,
            flags.current.reduced ? 0 : mouse.y,
          );
          gl.uniform1f(uniforms.aspect, width / height);
          gl.uniform1f(uniforms.pixelRatio, ratio);
          gl.uniform1f(uniforms.pointMode, 0);
          bindAttribute(lines, attributes.sample, 3);
          gl.drawArrays(gl.LINES, 0, vertices.length / 3);
          gl.uniform1f(uniforms.pointMode, 1);
          bindAttribute(points, attributes.sample, 3);
          gl.drawArrays(gl.POINTS, 0, particles.length / 3);
          gl.disable(gl.BLEND);
          blurPass(main, blurA, 3 / width, 0);
          blurPass(blurA, blurB, 0, 2 / blurA.height);
          bindTarget(null);
          gl.useProgram(composite);
          bindAttribute(quad, attributes.composite, 2);
          gl.activeTexture(gl.TEXTURE0);
          gl.bindTexture(gl.TEXTURE_2D, main.texture);
          gl.uniform1i(uniforms.source, 0);
          gl.activeTexture(gl.TEXTURE1);
          gl.bindTexture(gl.TEXTURE_2D, blurB.texture);
          gl.uniform1i(uniforms.bloom, 1);
          gl.uniform1f(uniforms.screenAspect, width / height);
          gl.drawArrays(gl.TRIANGLES, 0, 6);
        }
        if (animate) frame = requestAnimationFrame(draw);
      }
    };
    const request = () => {
      cancelAnimationFrame(frame);
      last = 0;
      if (!disposed) frame = requestAnimationFrame(draw);
    };
    requestRef.current = request;
    const resize = () => {
      ratio = Math.min(
        window.devicePixelRatio || 1,
        1.35,
        1600 / Math.max(stage.clientWidth, 1),
      );
      width = Math.max(1, Math.round(stage.clientWidth * ratio));
      height = Math.max(1, Math.round(stage.clientHeight * ratio));
      canvas.width = width;
      canvas.height = height;
      allocate(main, width, height);
      allocate(blurA, Math.max(1, width >> 2), Math.max(1, height >> 2));
      allocate(blurB, blurA.width, blurA.height);
      request();
    };
    const move = (event) => {
      if (event.pointerType === "touch") return;
      const r = stage.getBoundingClientRect();
      mouse.tx = ((event.clientX - r.left) / r.width) * 2 - 1;
      mouse.ty = 1 - ((event.clientY - r.top) / r.height) * 2;
    };
    const leave = () => {
      mouse.tx = 0;
      mouse.ty = 0;
    };
    const lost = (event) => {
      event.preventDefault();
      cancelAnimationFrame(frame);
      setReady(false);
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(stage);
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      request();
    });
    observer.observe(stage);
    stage.addEventListener("pointermove", move);
    stage.addEventListener("pointerleave", leave);
    canvas.addEventListener("webglcontextlost", lost);
    document.addEventListener("visibilitychange", request);
    resize();
    setReady(true);
    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      requestRef.current = null;
      resizeObserver.disconnect();
      observer.disconnect();
      stage.removeEventListener("pointermove", move);
      stage.removeEventListener("pointerleave", leave);
      canvas.removeEventListener("webglcontextlost", lost);
      document.removeEventListener("visibilitychange", request);
      clean();
    };
  }, []);
  return (
    <figure
      className="quantum-field"
      data-renderer={ready ? "webgl" : "static"}
      data-motion={playing && direction === "down" ? "playing" : "paused"}
    >
      <div ref={stageRef} className="field-stage" aria-hidden="true">
        <StaticField />
        <canvas ref={canvasRef} className={ready ? "is-ready" : ""} />
        <div className="field-vignette" />
      </div>
      <div className="field-topline">
        <span>
          <i />
          QUANTUMCODE
        </span>
        <span>PRECISION IN MOTION</span>
      </div>
      <figcaption className="field-caption">
        <div>
          <span className="field-eyebrow">CONSULTING + DEVELOPMENT</span>
          <p>From idea to application.</p>
        </div>
        <div className="field-controls">
          <span className="field-interaction">
            Move to explore
            <MoveUpRight size={13} />
          </span>
          {ready && (
            <button
              className="field-pause"
              onClick={() => {
                if (!playing) setMotionEnabled(true);
                setPaused(playing);
              }}
              aria-label={playing ? "Pause animation" : "Play animation"}
            >
              {playing ? <Pause size={15} /> : <Play size={15} />}
            </button>
          )}
        </div>
      </figcaption>
    </figure>
  );
}
